import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const VISIBLE_MS = 10000;
const HIDDEN_MS = 25000;

interface RedRockDrillsCtaSlideProps {
  /** CSS top value (e.g. '15%', '50%') */
  top: string;
  /** Delay in ms before first appearance */
  initialDelayMs: number;
}

export function RedRockDrillsCtaSlide({ top, initialDelayMs }: RedRockDrillsCtaSlideProps) {
  const [visible, setVisible] = useState(false);
  const dismissedRef = useRef(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dismissedRef.current = true;
    setVisible(false);
  };

  useEffect(() => {
    const show = () => {
      if (dismissedRef.current) return;
      setVisible(true);
    };
    const hide = () => setVisible(false);

    const runCycle = () => {
      if (dismissedRef.current) return;
      show();
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(hide, VISIBLE_MS);
    };

    const firstShowId = setTimeout(() => {
      runCycle();
      intervalRef.current = setInterval(runCycle, VISIBLE_MS + HIDDEN_MS);
    }, initialDelayMs);

    return () => {
      clearTimeout(firstShowId);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [initialDelayMs]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 22,
            mass: 0.8,
          }}
          className="fixed right-0 z-[100] pointer-events-auto"
          style={{ top }}
        >
          <div className="relative">
            <Link
              to="/redrock-guide"
              className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 rounded-l-2xl"
            >
              <motion.div
                initial={{ scale: 0.96 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                whileHover={{ scale: 1.02, transition: { type: 'spring', stiffness: 400, damping: 20 } }}
                whileTap={{ scale: 0.98, transition: { type: 'spring', stiffness: 500, damping: 30 } }}
                className="relative flex items-center gap-6 pl-7 pr-8 py-6 rounded-l-2xl overflow-hidden min-w-[340px] max-w-[420px] border border-l-0 border-slate-200/80 bg-white/90 backdrop-blur-xl"
                style={{
                  boxShadow:
                    '0 0 0 1px rgba(255,255,255,0.8) inset, 0 0 20px -4px rgba(59, 130, 246, 0.35), 0 0 40px -8px rgba(56, 189, 248, 0.2), 0 20px 40px -12px rgba(15, 23, 42, 0.12)',
                }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-blue-400 via-blue-500 to-blue-600 rounded-l-full shadow-[0_0_10px_rgba(59,130,246,0.6)]" aria-hidden />
                <div className="relative flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-md">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div className="relative flex-1 min-w-0">
                  <p className="text-lg font-semibold text-slate-900 leading-tight">
                    Try free Red Rock drills
                  </p>
                  <p className="text-base text-slate-600 mt-1">
                    Math, charts & logic · No sign-up to start
                  </p>
                </div>
                <div className="relative flex-shrink-0 text-blue-600">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </motion.div>
            </Link>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
